import { MakeLogicType, actions, afterMount, connect, kea, listeners, path, reducers, selectors } from 'kea'
import { router, urlToAction } from 'kea-router'

import { removeProjectIdIfPresent } from 'lib/utils/kea-router'

import { navigation3000Logic } from '../navigation-3000/navigationLogic'

export type PanelLayoutNavIdentifier =
    | 'Project'
    | 'Products'
    | 'People'
    | 'Games'
    | 'Shortcuts'
    | 'DataManagement'
    | 'DataAndPeople'
    | 'Chat'
    | 'Notifications'
export type NavExperimentTab = 'home' | 'chat'
export type PanelLayoutMainContentRef = React.RefObject<HTMLElement> | null
export const PANEL_LAYOUT_DEFAULT_WIDTH: number = 245
export const PANEL_LAYOUT_MIN_WIDTH: number = 160

// Navbar resize: any width upward, snapping to collapsed below the threshold.
export const PANEL_NAVBAR_DEFAULT_WIDTH: number = 215
// Below this the drag snaps to collapsed mode.
export const PANEL_NAVBAR_COLLAPSE_THRESHOLD: number = 140

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface panelLayoutLogicValues {
    mobileLayout: boolean // navigation3000Logic
    location: {
        pathname: string
        search: string
        hash: string
    } // router
    activePanelIdentifier: string
    activePanelIdentifierFromUrl: PanelLayoutNavIdentifier | ''
    activePanelIdentifierFromUrlAiFirst: PanelLayoutNavIdentifier | ''
    expandedNavSections: Record<string, boolean>
    isLayoutNavCollapsed: boolean
    isLayoutNavCollapsedDesktop: boolean
    isLayoutNavbarVisible: boolean
    isLayoutNavbarVisibleForDesktop: boolean
    isLayoutNavbarVisibleForMobile: boolean
    isLayoutPanelCloseable: boolean
    isLayoutPanelVisible: boolean
    mainContentRect: DOMRect | null
    mainContentRef: PanelLayoutMainContentRef
    navExperimentActiveTab: NavExperimentTab
    navbarWidth: number
    panelIsResizing: boolean
    panelWidth: number
    panelWillHide: boolean
    pathname: string
    sidePanelWidth: number
    visibleSideAction: string
    visitedNavTabs: NavExperimentTab[]
    visitedPanels: PanelLayoutNavIdentifier[]
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface panelLayoutLogicActions {
    clearActivePanelIdentifier: () => {
        value: true
    }
    closePanel: () => {
        value: true
    }
    resetPanelLayout: (keyboardAction: boolean) => {
        keyboardAction: boolean
    }
    setActivePanelIdentifier: (identifier: PanelLayoutNavIdentifier) => {
        identifier: PanelLayoutNavIdentifier
    }
    setMainContentRect: (rect: DOMRect) => {
        rect: DOMRect
    }
    setMainContentRef: (ref: PanelLayoutMainContentRef) => {
        ref: PanelLayoutMainContentRef
    }
    setNavExperimentTab: (tab: NavExperimentTab) => {
        tab: NavExperimentTab
    }
    setNavbarWidth: (width: number) => {
        width: number
    }
    setPanelIsResizing: (isResizing: boolean) => {
        isResizing: boolean
    }
    setPanelWidth: (width: number) => {
        width: number
    }
    setPanelWillHide: (willHide: boolean) => {
        willHide: boolean
    }
    setSidePanelWidth: (width: number) => {
        width: number
    }
    setVisibleSideAction: (sideAction: string) => {
        sideAction: string
    }
    showLayoutNavBar: (visible: boolean) => {
        visible: boolean
    }
    showLayoutPanel: (visible: boolean) => {
        visible: boolean
    }
    toggleLayoutNavCollapsed: (override?: boolean) => {
        override: boolean | undefined
    }
    toggleNavSection: (section: string) => {
        section: string
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface panelLayoutLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        isLayoutNavCollapsed: (isLayoutNavCollapsedDesktop: boolean, mobileLayout: boolean) => boolean
        activePanelIdentifierFromUrl: (location: {
            hash: string
            pathname: string
            search: string
        }) => PanelLayoutNavIdentifier | ''
        activePanelIdentifierFromUrlAiFirst: (location: {
            hash: string
            pathname: string
            search: string
        }) => PanelLayoutNavIdentifier | ''
        pathname: (location: { hash: string; pathname: string; search: string }) => string
    }
}

export type panelLayoutLogicType = MakeLogicType<
    panelLayoutLogicValues,
    panelLayoutLogicActions,
    Record<string, any>,
    panelLayoutLogicMeta
>

export const panelLayoutLogic = kea<panelLayoutLogicType>([
    path(['layout', 'panel-layout', 'panelLayoutLogic']),
    connect(() => ({
        values: [navigation3000Logic, ['mobileLayout'], router, ['location']],
    })),
    actions({
        closePanel: true,
        showLayoutNavBar: (visible: boolean) => ({ visible }),
        showLayoutPanel: (visible: boolean) => ({ visible }),
        // TODO: This is a temporary action to set the active navbar item
        // We should remove this once we have a proper way to handle the navbar item
        setActivePanelIdentifier: (identifier: PanelLayoutNavIdentifier) => ({ identifier }),
        clearActivePanelIdentifier: true,
        setMainContentRef: (ref: PanelLayoutMainContentRef) => ({ ref }),
        toggleLayoutNavCollapsed: (override?: boolean) => ({ override }),
        setVisibleSideAction: (sideAction: string) => ({ sideAction }),
        setPanelWidth: (width: number) => ({ width }),
        setPanelIsResizing: (isResizing: boolean) => ({ isResizing }),
        setPanelWillHide: (willHide: boolean) => ({ willHide }),
        resetPanelLayout: (keyboardAction: boolean) => ({ keyboardAction }),
        setMainContentRect: (rect: DOMRect) => ({ rect }),
        setSidePanelWidth: (width: number) => ({ width }),
        toggleNavSection: (section: string) => ({ section }),
        setNavExperimentTab: (tab: NavExperimentTab) => ({ tab }),
        setNavbarWidth: (width: number) => ({ width }),
    }),
    reducers({
        isLayoutNavbarVisibleForDesktop: [
            true,
            { persist: true },
            {
                showLayoutNavBar: (_, { visible }) => visible,
                mobileLayout: () => false,
            },
        ],
        isLayoutNavbarVisibleForMobile: [
            false,
            {
                showLayoutNavBar: (_, { visible }) => visible,
                mobileLayout: () => true,
            },
        ],
        isLayoutPanelCloseable: [
            true,
            {
                showLayoutPanel: () => true,
            },
        ],
        isLayoutNavbarVisible: [
            false,
            { persist: true },
            {
                showLayoutNavBar: (_, { visible }) => visible,
            },
        ],
        isLayoutPanelVisible: [
            false,
            { persist: true, prefix: '2', separator: '.' },
            {
                showLayoutPanel: (_, { visible }) => visible,
            },
        ],
        activePanelIdentifier: [
            '',
            { persist: true, prefix: '2', separator: '.' },
            {
                setActivePanelIdentifier: (_, { identifier }) => identifier,
                clearActivePanelIdentifier: () => '',
            },
        ],
        // Panels opened at least once this session. PanelLayoutPanels keeps these mounted (hidden)
        // so switching panels doesn't tear down and rebuild whole trees on every toggle.
        // Notifications is excluded: its logic drives unread/read semantics that must only run
        // while the panel is active, so keeping it mounted would be incorrect.
        visitedPanels: [
            [] as PanelLayoutNavIdentifier[],
            {
                setActivePanelIdentifier: (state, { identifier }) =>
                    identifier === 'Notifications' || state.includes(identifier) ? state : [...state, identifier],
            },
        ],
        // Nav tabs activated at least once this session. Nav renders the chat tab's keepMounted
        // panel only after first activation, so collapsed-nav users who never open chat don't
        // download and mount the lazy chat chunk on every app load.
        visitedNavTabs: [
            [] as NavExperimentTab[],
            {
                setNavExperimentTab: (state, { tab }) => (state.includes(tab) ? state : [...state, tab]),
            },
        ],
        mainContentRef: [
            null as PanelLayoutMainContentRef,
            {
                setMainContentRef: (_, { ref }) => ref,
            },
        ],
        isLayoutNavCollapsedDesktop: [
            false,
            { persist: true },
            {
                toggleLayoutNavCollapsed: (state, { override }) => override ?? !state,
            },
        ],
        visibleSideAction: [
            '',
            {
                setVisibleSideAction: (_, { sideAction }) => sideAction,
            },
        ],
        panelWidth: [
            PANEL_LAYOUT_DEFAULT_WIDTH,
            { persist: true, prefix: '2', separator: '.' },
            {
                setPanelWidth: (_, { width }) => width,
            },
        ],
        panelIsResizing: [
            false,
            {
                setPanelIsResizing: (_, { isResizing }) => isResizing,
            },
        ],
        panelWillHide: [
            false,
            {
                showLayoutPanel: (state, { visible }) => (visible ? false : state),
                setPanelWidth: (_, { width }) => width <= PANEL_LAYOUT_MIN_WIDTH - 1,
            },
        ],
        mainContentRect: [
            null as DOMRect | null,
            {
                setMainContentRect: (_, { rect }) => rect,
            },
        ],
        sidePanelWidth: [
            0,
            {
                setSidePanelWidth: (_, { width }) => width,
            },
        ],
        navExperimentActiveTab: [
            'home' as NavExperimentTab,
            { persist: true },
            {
                setNavExperimentTab: (_, { tab }) => tab,
            },
        ],
        // Transient: the live open width is restored from the resizer's persisted size on mount,
        // so persisting here would only duplicate it and thrash localStorage on every drag frame.
        navbarWidth: [
            PANEL_NAVBAR_DEFAULT_WIDTH,
            {
                setNavbarWidth: (_, { width }) => width,
            },
        ],
        expandedNavSections: [
            { ai: true, project: true, files: true, favorites: false, tools: true } as Record<string, boolean>,
            { persist: true, prefix: 'v2.' },
            {
                toggleNavSection: (state, { section }) => ({
                    ...state,
                    [section]: !state[section],
                }),
            },
        ],
    }),
    listeners(({ actions, values, cache }) => ({
        closePanel: () => {
            actions.showLayoutPanel(false)
            actions.clearActivePanelIdentifier()
        },
        setPanelIsResizing: ({ isResizing }) => {
            // If we're not resizing and the panel is at or below the minimum width, hide it
            if (!isResizing && values.panelWidth <= PANEL_LAYOUT_MIN_WIDTH - 1) {
                actions.showLayoutPanel(false)
                actions.clearActivePanelIdentifier()
                actions.setPanelWidth(PANEL_LAYOUT_DEFAULT_WIDTH)
            }
        },
        resetPanelLayout: ({ keyboardAction = false }) => {
            // Hide the panel and clear active panel identifier
            actions.clearActivePanelIdentifier()
            actions.showLayoutPanel(false)
            // Hide the navbar if it's mobile and navbar is visible (which is an overlay on mobile)
            if (values.mobileLayout && values.isLayoutNavbarVisible) {
                actions.showLayoutNavBar(false)
            }
            // Focus the main content if it's a keyboard action
            if (keyboardAction && values.mainContentRef?.current) {
                values.mainContentRef?.current?.focus()
            }
        },
        setMainContentRef: ({ ref }) => {
            // Measure width immediately when container ref is set
            if (ref?.current) {
                actions.setMainContentRect(ref.current.getBoundingClientRect())

                // Set up new ResizeObserver for the new container
                if (typeof ResizeObserver !== 'undefined') {
                    cache.disposables.add(() => {
                        // ref.current may be null when disposable resumes after visibility change
                        if (!ref.current) {
                            return () => {}
                        }
                        const observer = new ResizeObserver(() => {
                            if (ref?.current) {
                                actions.setMainContentRect(ref.current.getBoundingClientRect())
                            }
                        })
                        observer.observe(ref.current)
                        return () => observer.disconnect()
                    }, 'resizeObserver')
                }
            }
        },
    })),
    selectors({
        isLayoutNavCollapsed: [
            (s) => [s.isLayoutNavCollapsedDesktop, s.mobileLayout],
            (isLayoutNavCollapsedDesktop: boolean, mobileLayout: boolean): boolean =>
                !mobileLayout && isLayoutNavCollapsedDesktop,
        ],
        activePanelIdentifierFromUrl: [
            () => [router.selectors.location],
            (location: { hash: string; pathname: string; search: string }): PanelLayoutNavIdentifier | '' => {
                const cleanPath = removeProjectIdIfPresent(location.pathname)

                if (cleanPath.startsWith('/data-management/')) {
                    return 'DataManagement'
                }

                if (cleanPath === '/persons' || cleanPath === '/cohorts' || cleanPath.startsWith('/groups/')) {
                    return 'People'
                }

                return ''
            },
        ],
        activePanelIdentifierFromUrlAiFirst: [
            () => [router.selectors.location],
            (location: { hash: string; pathname: string; search: string }): PanelLayoutNavIdentifier | '' => {
                const cleanPath = removeProjectIdIfPresent(location.pathname)

                if (
                    cleanPath.startsWith('/data-management/') ||
                    cleanPath === '/persons' ||
                    cleanPath === '/cohorts' ||
                    cleanPath.startsWith('/groups/')
                ) {
                    return 'DataAndPeople'
                }

                return ''
            },
        ],
        pathname: [
            (s) => [s.location],
            (location: { hash: string; pathname: string; search: string }): string => location.pathname,
        ],
    }),
    urlToAction(({ actions, values }) => ({
        '*': () => {
            // URL-surfaced panels (DataAndPeople, DataManagement) set activePanelIdentifier
            // without flipping isLayoutPanelVisible, so check the identifier too — otherwise
            // navigating away from one leaves the panel and its dim mounted.
            const panelIsShown = values.isLayoutPanelVisible || values.activePanelIdentifier !== ''
            if (values.mobileLayout && (values.isLayoutNavbarVisibleForMobile || panelIsShown)) {
                actions.showLayoutNavBar(false)
                actions.showLayoutPanel(false)
                actions.clearActivePanelIdentifier()
            }
        },
    })),
    afterMount(({ actions, cache, values }) => {
        // visitedPanels is not persisted, but activePanelIdentifier is. Seed visitedPanels from
        // the rehydrated identifier so the panel that was open on the previous session starts
        // keep-mounted — without this, the first switch after reload tears the panel down once.
        if (values.activePanelIdentifier) {
            actions.setActivePanelIdentifier(values.activePanelIdentifier as PanelLayoutNavIdentifier)
        }
        // Same for the persisted nav tab: a user who was on the chat tab last session gets its
        // panel mounted from the start.
        actions.setNavExperimentTab(values.navExperimentActiveTab)

        // Watch for window resize
        if (typeof window !== 'undefined') {
            cache.disposables.add(() => {
                const handleResize = (): void => {
                    const mainContentRef = values.mainContentRef
                    if (mainContentRef?.current) {
                        actions.setMainContentRect(mainContentRef.current.getBoundingClientRect())
                    }
                }
                window.addEventListener('resize', handleResize)
                return () => window.removeEventListener('resize', handleResize)
            }, 'windowResize')
        }
    }),
])
